import CustomIcon from "components/CustomIcon";
import { Input, Textarea } from "components/ResumeForm/Form/InputGroup";
import { useAppSelector } from "lib/redux/hooks";
import { changeProfile, selectProfile } from "lib/redux/resumeSlice";
import { ResumeProfile } from "lib/redux/types";
import { ResumeAnalysis } from "lib/resume-scoring/resume-analysis/resume-analyse-types";
import Image from "next/image";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { MistakesSection } from "../common/MistakesSection";
import { NoIssueBox } from "../common/NoIssueBox";
import { PassiveVoice } from "../common/PassiveVoice";
import { PersonalPronouns } from "../common/PersonalPronouns";
import { SectionHeader } from "../common/SectionHeader";
import { WordMistakesSection } from "../common/WordMistakesSection";
import { TextInputSection } from "../common/TextInputSection";
import { PersonalDetailsScoreAnalysis } from "lib/resume-scoring/types";
import { MistakesDisplayV2 } from "../common/MistakesDisplayV2";
import { highlightSection, clearHighlight } from "lib/redux/highlightSlice";
import { FixButtons } from "../common/FixButtons";

export const PersonalSection = ({
  score,
  maxScore,
  analysis,
}: {
  score: PersonalDetailsScoreAnalysis;
  maxScore: number;
  analysis: ResumeAnalysis["profile"];
}) => {
  const [isAddLinksOpen, setIsAddLinksOpen] = useState(false);
  const [hasLinkAdded, setHasLinkAdded] = useState(false);
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

  const profile = useAppSelector(selectProfile);
  const { name, email, phone, url1, url2, summary, location } = profile;
  const dispatch = useDispatch();

  const getHasDescriptionIssues = (): boolean => {
    if (analysis.summaryAnalysis.statement == "") return true;
    if (analysis.summaryAnalysis.analysis.buzzWords.length > 0) return true;
    if (analysis.summaryAnalysis.analysis.fillerWords.length > 0) return true;
    if (analysis.summaryAnalysis.analysis.spellingMistakes.length > 0)
      return true;
    if (analysis.summaryAnalysis.analysis.grammarMistakes.length > 0)
      return true;

    return false;
  };

  const hasStatementIssue = getHasDescriptionIssues();

  // Filter out null/falsy values and deduplicate for Personal section
  const spellingMistakes = [
    ...new Set(
      (analysis.summaryAnalysis.analysis.spellingMistakes
        ?.map((mistake) => mistake.incorrect)
        .filter(Boolean) as string[]) || []
    ),
  ];

  const buzzWords = [
    ...new Set(
      (analysis.summaryAnalysis.analysis.buzzWords?.filter(
        Boolean
      ) as string[]) || []
    ),
  ];

  // Grammar mistakes - filter out null/falsy values and deduplicate
  const grammarMistakes = [
    ...new Set(
      (analysis.summaryAnalysis.analysis.grammarMistakes
        ?.map((m) => m.incorrect)
        .filter(Boolean) as string[]) || []
    ),
  ];

  // Personal pronouns - deduplicate
  const personalPronouns = [
    ...new Set(
      (analysis.summaryAnalysis.analysis.personalPronouns?.filter(
        Boolean
      ) as string[]) || []
    ),
  ];

  // Repeated words detection in the summary statement
  // logic: case-insensitive, strip punctuation, ignore short words and common stopwords,
  // then return words that occur more than once.
  const repeatedWords: string[] = (() => {
    const statement = (analysis.summaryAnalysis.statement || "").toString();
    if (!statement) return [];

    const stopwords = new Set([
      "the",
      "and",
      "to",
      "of",
      "a",
      "in",
      "for",
      "with",
      "on",
      "at",
      "by",
      "is",
      "are",
      "was",
      "were",
      "be",
      "or",
      "that",
      "this",
      "it",
      "as",
      "an",
      "from",
      "have",
      "has",
      "had",
      "i",
      "my",
      "me",
      "we",
      "our",
      "you",
      "your",
      "but",
      "not",
      "will",
      "can",
      "would",
      "should",
    ]);
    // Keep letters/numbers and apostrophes/hyphens inside words (ASCII fallback)
    const cleaned = statement.replace(/[^A-Za-z0-9\s'-]+/g, ' ');
    const tokens = cleaned
      .toLowerCase()
      .split(/\s+/)
      .map((t) => t.replace(/^['-]+|['-]+$/g, ''))
      .filter((t) => t.length >= 3 && !stopwords.has(t));

    const counts: Record<string, number> = {};
    for (const t of tokens) {
      counts[t] = (counts[t] || 0) + 1;
    }

    return Object.keys(counts).filter((w) => counts[w] > 1);
  })();

  const handleProfileChange = (field: keyof ResumeProfile, value: string) => {
    dispatch(changeProfile({ field, value }));
  };

  // Section-level highlighting handlers for Personal section
  const handleSectionMouseEnter = () => {
    dispatch(
      highlightSection({
        section: "profile",
        itemIndex: null, // Personal section has no individual items
      })
    );
  };

  const handleSectionMouseLeave = () => {
    dispatch(clearHighlight());
  };

  const isMissingInfo =
    !analysis.name ||
    !analysis.email ||
    !analysis.phone ||
    !analysis.location ||
    !analysis.url1 ||
    !analysis.summaryAnalysis.statement ||
    !analysis.url2;

  const showFix = isMissingInfo ||
   hasStatementIssue || grammarMistakes.length > 0
   || spellingMistakes.length > 0 || buzzWords.length > 0
   || personalPronouns.length > 0 || analysis.summaryAnalysis.analysis.isPassiveVoice == 1
   ;

  return (
    <div className="scrollbar-hide relative mb-4 flex h-full max-h-[88.5vh] flex-col rounded-[12px] bg-white p-1 shadow-md sm:p-2 lg:rounded-[6px] lg:p-1 xl:p-2">
      {/* Fixed Sticky Header */}
      <SectionHeader
        title="Personal"
        score={score.totalScore}
        maxScore={maxScore}
      />
      <span
        className="sticky top-0 z-10 mx-auto my-2 hidden cursor-pointer  items-center justify-center gap-3 rounded-xl bg-gray-100 px-[16px] py-[4px] text-center text-sm font-semibold text-black shadow-md transition-opacity hover:opacity-90 md:text-lg lg:flex lg:px-[20px] lg:py-[8px] xl:px-[24px] xl:text-xl"
        onMouseEnter={handleSectionMouseEnter}
        onMouseLeave={handleSectionMouseLeave}
      >
        Personal Details
      </span>

      {/* Scrollable Content - Hidden Scrollbar */}
      <div className="scrollbar-hide flex flex-1 flex-col gap-[25px] overflow-auto px-0 pb-3 lg:gap-[30px] lg:px-0 xl:gap-[40px]">
        {isMissingInfo && (
          <div className="relative mt-8 flex flex-col gap-[10px] rounded-xl bg-[#FEE7E4] px-[15px] py-[10px] lg:gap-[16px] lg:px-[20px] lg:py-[14px]">
            <div className="absolute left-4 top-[-16px] flex items-center gap-2 rounded-xl bg-white px-4 py-2 font-bold text-black shadow-md lg:left-8 lg:px-6">
              <Image
                src="/icons/score-check/missing.svg"
                height={24}
                width={24}
                alt="icon"
                className="h-4 w-4 lg:h-5 lg:w-5"
              />
              <span className="text-sm font-bold lg:text-base xl:text-lg">
                Missing Information
              </span>
            </div>

            <div className="mt-6">
              <div className="flex flex-col gap-2">
                {/* Missing Name */}
                {!analysis.name && (
                  <TextInputSection
                    value={name}
                    placeholder="Alex Johnson"
                    fieldName="name"
                    missingText="Name missing"
                    handleChange={handleProfileChange}
                    icon="link"
                  />
                )}

                {/* Missing Email */}
                {!analysis.email && (
                  <TextInputSection
                    value={email}
                    placeholder="john@mail.com"
                    fieldName="email"
                    missingText="Email missing"
                    handleChange={handleProfileChange}
                    icon="link"
                  />
                )}

                {/* Missing Phone */}
                {!analysis.phone && (
                  <TextInputSection
                    value={phone}
                    placeholder="(123) 456-7890"
                    fieldName="phone"
                    missingText="Phone missing"
                    handleChange={handleProfileChange}
                    icon="link"
                  />
                )}

                {/* Missing Location */}
                {!analysis.location && (
                  <TextInputSection
                    value={location}
                    placeholder="San Francisco, CA"
                    fieldName="location"
                    missingText="Location missing"
                    handleChange={handleProfileChange}
                    icon="link"
                  />
                )}

                {/* Missing Links */}
                {(!analysis.url1 || !analysis.url2) && (
                  <div
                    className="flex items-center rounded-xl px-[6px] py-[8px] sm:px-[9px] sm:py-[10px] xl:p-[15px]"
                    style={{
                      backgroundColor:
                        isEditingLink || hasLinkAdded ? "white" : "#F8F8F8",
                      border:
                        isEditingLink || hasLinkAdded
                          ? "3px solid #F8F8F8"
                          : "3px solid #FFFFFF",
                    }}
                  >
                    <div className="flex w-full items-center justify-start gap-[10px] sm:gap-[15px] lg:gap-[30px]">
                      <div className="size-[16px] sm:size-[20px] lg:size-[20px] xl:size-[20px] rounded-full bg-[#EDAD53] px-[3px] py-[3px] sm:px-[5px] sm:py-[5px]">
                        <Image
                          src="/icons/duration.svg"
                          width={15}
                          height={15}
                          alt="icon"
                          className="h-2 w-2 xl:h-4 xl:w-4"
                        />
                      </div>
                      <span className="font-montserrat text-[10px] font-medium text-black xs:text-xs sm:text-sm md:text-base lg:text-[10px] xl:text-[16px]">
                        {isEditingLink ? "Edit Links" : "Links missing"}
                      </span>
                      {/* Desktop: Show title and inputs separately */}
                      {/* <div className="hidden w-full items-center justify-start lg:flex">
                        {!hasLinkAdded ? (
                          <span className="font-montserrat text-[10px] font-medium text-black xs:text-xs sm:text-sm md:text-base lg:text-[10px] xl:text-[16px]">
                            {isEditingLink ? "Edit Links" : "Links missing"}
                          </span>
                        ) : (
                          <div className="flex w-full items-center gap-4">
                            <Input
                              label=""
                              labelClassName="col-span-3"
                              name="url1"
                              placeholder="https://linkedin.com/in/maverick"
                              value={url1}
                              onChange={handleProfileChange}
                              disabled
                            />
                            <Input
                              label=""
                              labelClassName="col-span-3"
                              name="url2"
                              placeholder="https://github.com/maverick"
                              value={url2}
                              onChange={handleProfileChange}
                              disabled
                            />
                          </div>
                        )}
                      </div> */}

                      {/* Mobile: Show title OR inputs */}
                      {/* <div className="flex w-full items-center justify-start lg:hidden">
                        {!isEditingLink && !hasLinkAdded ? (
                          <span className="font-montserrat text-[12px] font-medium text-black">
                            Links missing
                          </span>
                        ) : !isEditingLink && hasLinkAdded ? (
                          <div className="flex w-full flex-col items-center justify-center gap-1">
                            <Input
                              label=""
                              labelClassName="col-span-3"
                              name="url1"
                              placeholder="https://linkedin.com/in/maverick"
                              value={url1}
                              onChange={handleProfileChange}
                              disabled
                            />
                            <Input
                              label=""
                              labelClassName="col-span-3"
                              name="url2"
                              placeholder="https://github.com/maverick"
                              value={url2}
                              onChange={handleProfileChange}
                              disabled
                            />
                          </div>
                        ) : (
                          <div className="flex w-full flex-col items-center justify-center gap-1">
                            <Input
                              label=""
                              labelClassName="col-span-3"
                              name="url1"
                              placeholder="https://linkedin.com/in/maverick"
                              value={url1}
                              onChange={handleProfileChange}
                            />
                            <Input
                              label=""
                              labelClassName="col-span-3"
                              name="url2"
                              placeholder="https://github.com/maverick"
                              value={url2}
                              onChange={handleProfileChange}
                            />
                          </div>
                        )}
                      </div> */}
                    </div>

                    {/* Desktop editing inputs (when isAddLinksOpen) */}
                    {/* {isAddLinksOpen && (
                      <div
                        style={{
                          visibility: hasLinkAdded ? "hidden" : "visible",
                        }}
                        className="hidden w-full items-center justify-center gap-3 lg:flex"
                      >
                        <Input
                          label=""
                          labelClassName="col-span-3"
                          name="url1"
                          placeholder="https://linkedin.com/in/maverick"
                          value={url1}
                          onChange={handleProfileChange}
                        />
                        <Input
                          label=""
                          labelClassName="col-span-3"
                          name="url2"
                          placeholder="https://github.com/maverick"
                          value={url2}
                          onChange={handleProfileChange}
                        />
                      </div>
                    )} */}

                    {/* Edit button */}
                    {/* <button
                      onClick={() => {
                        setIsEditingLink(true);
                        setHasLinkAdded(false);
                      }}
                      className="rounded-full border bg-white shadow-md xl:p-1"
                      style={{
                        visibility: hasLinkAdded ? "visible" : "hidden",
                      }}
                    >
                      <CustomIcon iconName="edit" />
                    </button> */}

                    {/* Add/Added button */}
                    {/* <button
                      onClick={() => {
                        setIsAddLinksOpen(true);
                        setIsEditingLink(true);
                        if (isAddLinksOpen) {
                          setHasLinkAdded(true);
                        }
                      }}
                      className={`ml-1 flex items-center gap-1 rounded-lg py-1 font-poppins
                        text-[10px] font-medium text-[#458ef7] shadow-xl transition-all duration-200 hover:bg-gray-100 
                        sm:ml-2 sm:py-1.5 sm:text-[12px] lg:ml-1
                        lg:py-1.5 lg:text-[10px] xl:text-[16px]
                        ${
                          hasLinkAdded
                            ? "bg-transparent px-2 sm:px-2.5 lg:px-2 xl:px-3"
                            : "bg-white px-3 sm:px-6 lg:px-3 xl:px-9"
                        }`}
                      disabled={hasLinkAdded && !isEditingLink}
                    >
                      {hasLinkAdded ? "Added" : "Add"}
                      {hasLinkAdded && (
                        <CustomIcon
                          iconName="greenTick"
                          className="h-3 w-3 sm:h-4 sm:w-4"
                        />
                      )}
                    </button> */}
                  </div>
                )}
                {/* Missing Description */}
                {!analysis.summaryAnalysis.statement && (
                  <div
                    className="flex items-center rounded-xl border-[3px] border-white px-[6px] py-[8px] sm:px-[9px] sm:py-[10px] xl:p-[15px]"
                    style={{ backgroundColor: "#F8F8F8" }}
                  >
                    <div className="flex w-full items-center justify-start gap-[15px] lg:gap-[30px]">
                      <div className="size-[16px] sm:size-[20px] lg:size-[20px] xl:size-[20px] rounded-full  bg-[#EDAD53] px-[3px] py-[3px] sm:px-[5px] sm:py-[5px]">
                        <Image
                          src="/icons/duration.svg"
                          width={15}
                          height={15}
                          alt="date"
                          className="h-2 w-2 lg:h-4 lg:w-4"
                        />
                      </div>

                      {/* Desktop: Show title */}
                      <div className="hidden w-full items-center justify-start lg:flex">
                        <span className="font-montserrat text-[10px] font-medium text-black xs:text-xs sm:text-sm md:text-base lg:text-[10px] xl:text-[16px]">
                          Summary/Objective missing
                        </span>
                      </div>

                      {/* Mobile: Show title */}
                      <div className="flex w-full items-center justify-start lg:hidden">
                        <span className="font-montserrat text-[12px] font-medium text-black">
                          Summary/Objective missing
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-col items-center justify-center gap-3">
              <div className="relative h-32 w-32 lg:h-40 lg:w-40">
                <Image
                  src="/assets/resumeScore/missingInfo.png"
                  alt="Long sentences illustration"
                  fill
                  className="object-contain"
                />
              </div>
              <p className="text-center text-sm font-semibold text-red-600 lg:text-base">
                Missing information can weaken your resume
              </p>
            </div>
          </div>
        )}
        {!isMissingInfo && (
          <div className="relative mt-4 flex flex-col gap-[8px] rounded-xl bg-[#FEE7E4] px-[10px] py-[8px] sm:gap-[10px] sm:px-[15px] sm:py-[10px] md:mt-3 lg:gap-[16px] lg:px-[20px] lg:py-[14px]">
            <div className="absolute left-4 top-[-16px] flex items-center  gap-2 rounded-xl bg-white px-4 py-2 font-bold text-black shadow-md lg:left-8 lg:px-6">
              <Image
                src={`/icons/score-check/missing.svg`}
                height={24}
                width={24}
                alt="icon"
                className="h-4 w-4 lg:h-5 lg:w-5"
              />
              <span className="text-sm font-bold lg:text-base xl:text-lg">
                No Missing Information
              </span>
            </div>
            <div className="mt-5 flex flex-col items-center justify-center gap-1">
              {/* <p className="text-center my-2 text-sm font-semibold text-green-600 lg:text-base">
                          </p> */}
              <div className="relative my-2 h-32 w-44">
                <Image
                  src="/assets/resumeScore/NoMissingInfo.png"
                  alt="Word mistakes illustration"
                  fill
                  className="w-15 h-10"
                />
              </div>
              <p className="text-center text-xs text-gray-600 lg:text-sm">
                Great job ! Your personal details are complete.
              </p>
            </div>
          </div>
        )}

        {/* Spelling, Buzz Words (from summary) */}
        {analysis.summaryAnalysis.statement && (
          <MistakesDisplayV2
            spellingMistakes={spellingMistakes}
            buzzWords={buzzWords}
            repeatedWords={repeatedWords}
            section="profile"
            itemIndex={null}
          />
        )}

        {/* Issues */}
        {/* {hasStatementIssue && analysis.summaryAnalysis.statement !== "" && (
          <MistakesSection mistakes={[analysis.summaryAnalysis.statement]} />
        )} */}

        {/* Grammar Mistakes */}
        {grammarMistakes.length > 0 && (
          <WordMistakesSection
            title="Grammar mistake"
            icon="grammarMistake"
            section="profile"
            itemIndex={null}
            mistakeType="grammar"
            mistakes={grammarMistakes}
          />
        )}

        {/* Spelling Mistakes */}
        {/* {analysis.summaryAnalysis.analysis.spellingMistakes.length > 0 && (
          <WordMistakesSection
            title="Spelling mistake"
            icon="spellingMistake"
            mistakes={analysis.summaryAnalysis.analysis.spellingMistakes.map(
              (mistake) => mistake.incorrect
            )}
          />
        )} */}

        {/* Buzz Words */}
        {/* {analysis.summaryAnalysis.analysis.buzzWords.length > 0 && (
          <WordMistakesSection
            title="Buzz words found"
            icon="spellingMistake"
            mistakes={analysis.summaryAnalysis.analysis.buzzWords}
          />
        )} */}

        {personalPronouns.length > 0 && (
          <PersonalPronouns
            personalPronouns={personalPronouns}
            section="profile"
            itemIndex={null}
          />
        )}

        {/* Passive Voice */}
        {analysis.summaryAnalysis.analysis.isPassiveVoice == 1 && (
          <PassiveVoice />
        )}

        {/* Manual / AI Fix Buttons */}
        {showFix && (
          <>
            {isDescriptionOpen && (
              <div className="m-2 mx-auto flex w-full  flex-col justify-center gap-3 rounded-xl bg-white p-4 shadow-sm">
                <Input
                  label="Name"
                  labelClassName="col-span-full"
                  name="name"
                  placeholder="Alex Maverick"
                  value={name}
                  onChange={handleProfileChange}
                />
                <Textarea
                  label="Summary"
                  labelClassName="col-span-full w-full"
                  name="summary"
                  placeholder="Crafting digital experiences with a sprinkle of creativity."
                  value={summary}
                  onChange={handleProfileChange}
                />
                <div className="grid w-full grid-cols-2 gap-3">
                  <Input
                    label="Email"
                    labelClassName="col-span-1"
                    name="email"
                    placeholder="alex@creativemind.io"
                    value={email}
                    onChange={handleProfileChange}
                  />
                  <Input
                    label="Phone"
                    labelClassName="col-span-1"
                    name="phone"
                    placeholder="(123)456-7890"
                    value={phone}
                    onChange={handleProfileChange}
                  />
                </div>
                <Input
                  label="Website 1"
                  labelClassName="col-span-full"
                  name="url1"
                  placeholder="https://github.com/maverick"
                  value={url1}
                  onChange={handleProfileChange}
                />
                <div className="grid w-full grid-cols-2 gap-3">
                  <Input
                    label="Website 2"
                    labelClassName="col-span-1"
                    name="url2"
                    placeholder="https://linkedin.com/in/maverick"
                    value={url2}
                    onChange={handleProfileChange}
                  />
                  <Input
                    label="Location"
                    labelClassName="col-span-1"
                    name="location"
                    placeholder="Delhi, India"
                    value={location}
                    onChange={handleProfileChange}
                  />
                </div>
              </div>
            )}

            {/* <div className="flex w-full items-center justify-center gap-2 sm:gap-3 md:gap-5 lg:gap-10"> */}
            {/* <button className="rounded-full flex items-center gap-1 sm:gap-2 lg:gap-3 justify-center shadow-md bg-gradient-to-r to-[#0085FF] from-[#88C4FC] px-3 sm:px-5 md:px-6 lg:px-[36px] py-1 sm:py-2 lg:py-[8px] text-xs sm:text-sm md:text-base lg:text-lg font-medium text-white">
                <span className="xs:inline">Fix with</span> Ai
                <CustomIcon iconName="ai" className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
              </button> */}
            {/* <button
                onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
                className="flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-medium text-black shadow-md sm:gap-2 sm:px-5 sm:py-2 sm:text-sm md:px-6 md:text-base lg:gap-3 lg:px-[36px] lg:py-[8px] lg:text-lg"
              >
                {isDescriptionOpen ? (
                  "Save"
                ) : (
                  <span className="whitespace-nowrap">
                    {window.innerWidth < 350 ? "Fix" : "Fix"}
                  </span>
                )}
                <CustomIcon
                  iconName="importBlack"
                  className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5"
                />
              </button>
            </div> */}
            <FixButtons
              isEditMode={isDescriptionOpen}
              toggleEditMode={() => setIsDescriptionOpen(!isDescriptionOpen)}
            />
          </>
        )}
        {!hasStatementIssue && (
          <>
            <NoIssueBox message="No issues found in the summary. Great job!" />
          </>
        )}
      </div>

      {/* Add custom scrollbar hide styles */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};
